import * as THREE from 'three';
import type { LegacyShadowQuality } from '../settings/LegacySystemSettings';

const LEGACY_SHADOW_OFFSET = 0.0015;

type CompilableShader = {
  fragmentShader: string;
  uniforms: Record<string, THREE.IUniform>;
};

/**
 * Keeps the source shadow selector as a uniform so a System-panel preview can
 * switch kernels without recompiling every streamed world material.
 */
export class LegacyShadowSampling {
  private readonly qualityUniform: THREE.IUniform<number>;

  constructor(quality: LegacyShadowQuality = 'high') {
    this.qualityUniform = { value: legacyShadowKernelQuality(quality) };
  }

  setQuality(quality: LegacyShadowQuality): void {
    this.qualityUniform.value = legacyShadowKernelQuality(quality);
  }

  inject(shader: CompilableShader): void {
    shader.uniforms.legacyShadowKernelQuality = this.qualityUniform;
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <shadowmap_pars_fragment>',
      `#include <shadowmap_pars_fragment>
${LEGACY_SHADOW_FILTER_GLSL}`,
    );
  }
}

/** `abw.L()`: quality zero uses 512²; every higher enabled quality uses 2048². */
export function legacyShadowMapSize(quality: LegacyShadowQuality): number {
  switch (quality) {
    case 'off':
      return 0;
    case 'low':
      return 512;
    case 'medium':
    case 'high':
    case 'extreme':
      return 2048;
  }
}

/** `LI.c()`: zero/one share one lookup, two uses 3×3, and three uses 17 lookups. */
export function legacyShadowKernelQuality(quality: LegacyShadowQuality): number {
  switch (quality) {
    case 'off':
    case 'low':
    case 'medium':
      return 1;
    case 'high':
      return 2;
    case 'extreme':
      return 3;
  }
}

/**
 * `phongshadows*.frag` samples a linearly filtered comparison texture at
 * source-fixed projected offsets. The source deliberately uses rounded
 * normalization constants (0.111 and 0.0588), which are retained here.
 */
const LEGACY_SHADOW_FILTER_GLSL = `
#if defined(USE_SHADOWMAP) && defined(SHADOWMAP_TYPE_PCF)
uniform int legacyShadowKernelQuality;

float legacyWorldShadowLookup(
  sampler2DShadow shadowMap,
  vec2 coordinate,
  float comparisonDepth
) {
  return texture(shadowMap, vec3(coordinate, comparisonDepth));
}

float getLegacyWorldShadow(
  sampler2DShadow shadowMap,
  vec2 shadowMapSize,
  float shadowIntensity,
  float shadowBias,
  float shadowRadius,
  vec4 shadowCoord
) {
  shadowCoord.xyz /= shadowCoord.w;
  shadowCoord.z += shadowBias;

  bool inFrustum =
    shadowCoord.x >= 0.0 && shadowCoord.x <= 1.0 &&
    shadowCoord.y >= 0.0 && shadowCoord.y <= 1.0;
  bool frustumTest = inFrustum && shadowCoord.z <= 1.0;
  float shadow = 1.0;

  if (frustumTest) {
    vec2 projected = shadowCoord.xy;
    float depth = shadowCoord.z;
    if (legacyShadowKernelQuality >= 3) {
      shadow =
        legacyWorldShadowLookup(shadowMap, projected + vec2( ${LEGACY_SHADOW_OFFSET.toFixed(4)}, -${LEGACY_SHADOW_OFFSET.toFixed(4)}), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2( ${LEGACY_SHADOW_OFFSET.toFixed(4)},  0.0000), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2( ${LEGACY_SHADOW_OFFSET.toFixed(4)},  ${LEGACY_SHADOW_OFFSET.toFixed(4)}), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2( 0.0000, -${LEGACY_SHADOW_OFFSET.toFixed(4)}), depth) +
        legacyWorldShadowLookup(shadowMap, projected, depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2( 0.0000,  ${LEGACY_SHADOW_OFFSET.toFixed(4)}), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2(-${LEGACY_SHADOW_OFFSET.toFixed(4)}, -${LEGACY_SHADOW_OFFSET.toFixed(4)}), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2(-${LEGACY_SHADOW_OFFSET.toFixed(4)},  0.0000), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2(-${LEGACY_SHADOW_OFFSET.toFixed(4)},  ${LEGACY_SHADOW_OFFSET.toFixed(4)}), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2( 0.0030, -0.0030), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2( 0.0030,  0.0000), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2( 0.0030,  0.0030), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2( 0.0000,  0.0030), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2( 0.0000, -0.0030), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2(-0.0030, -0.0030), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2(-0.0030,  0.0000), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2(-0.0030,  0.0030), depth);
      shadow *= 0.0588;
    } else if (legacyShadowKernelQuality == 2) {
      shadow =
        legacyWorldShadowLookup(shadowMap, projected + vec2( ${LEGACY_SHADOW_OFFSET.toFixed(4)}, -${LEGACY_SHADOW_OFFSET.toFixed(4)}), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2( ${LEGACY_SHADOW_OFFSET.toFixed(4)},  0.0000), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2( ${LEGACY_SHADOW_OFFSET.toFixed(4)},  ${LEGACY_SHADOW_OFFSET.toFixed(4)}), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2( 0.0000, -${LEGACY_SHADOW_OFFSET.toFixed(4)}), depth) +
        legacyWorldShadowLookup(shadowMap, projected, depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2( 0.0000,  ${LEGACY_SHADOW_OFFSET.toFixed(4)}), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2(-${LEGACY_SHADOW_OFFSET.toFixed(4)}, -${LEGACY_SHADOW_OFFSET.toFixed(4)}), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2(-${LEGACY_SHADOW_OFFSET.toFixed(4)},  0.0000), depth) +
        legacyWorldShadowLookup(shadowMap, projected + vec2(-${LEGACY_SHADOW_OFFSET.toFixed(4)},  ${LEGACY_SHADOW_OFFSET.toFixed(4)}), depth);
      shadow *= 0.111;
    } else {
      shadow = legacyWorldShadowLookup(shadowMap, projected, depth);
    }
  }

  return mix(1.0, shadow, shadowIntensity);
}

#define getShadow getLegacyWorldShadow
#endif
`;
